import { useState, useEffect, useRef } from 'react';

const cards = [
  {
    id: 'build',
    icon: 'ri-code-s-slash-line',
    title: 'BUILD',
    subtitle: 'I build your website.',
    description:
      'You see this website and how clean and professional it looks? I made this. I can make one for you too. I take your vision, your brand, and your goals — and turn it into a real, polished website that makes people take you seriously the moment they land on it.',
    accent: 'From idea to a fully designed, professional website.',
  },
  {
    id: 'host',
    icon: 'ri-cloud-line',
    title: 'HOST',
    subtitle: 'I host your website.',
    description:
      'Once your website is built, it needs a home on the internet — that\u2019s hosting. Think of it like rent for your website\u2019s address. Every month, there\u2019s a small cost to keep your site live, fast, and accessible to anyone who searches for you. I handle all of that so you don\u2019t have to think about it.',
    accent: 'Your site stays live, fast, and always online.',
  },
  {
    id: 'manage',
    icon: 'ri-settings-4-line',
    title: 'MANAGE',
    subtitle: 'I manage your website.',
    description:
      'Life changes. Your business evolves. When you need something updated — new photos, new text, a whole new section — you just put in a request and I\u2019ll get it done.',
    accent: 'Request changes anytime. I handle the rest.',
  },
];

export default function WhatIDo() {
  const [expandedCard, setExpandedCard] = useState<string | null>('build');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const toggleCard = (id: string) => {
    setExpandedCard((prev) => (prev === id ? null : id));
  };

  return (
    <section
      id="whatido"
      ref={sectionRef}
      className="py-12 sm:py-20 bg-[#FAFAF8] font-body relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#F5C518]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#F5C518]/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <div
          className={`text-center mb-10 sm:mb-14 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[1px] bg-[#F5C518]"></div>
            <span className="text-[#F5C518] text-xs font-semibold tracking-[0.3em] uppercase">
              What I Do
            </span>
            <div className="w-8 h-[1px] bg-[#F5C518]"></div>
          </div>
          <h2 className="text-4xl sm:text-6xl md:text-7xl font-display tracking-wide text-[#1a1a1a]">
            BUILD. HOST. MANAGE.
          </h2>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-3 gap-5 sm:gap-6 items-start">
          {cards.map((card, i) => {
            const isExpanded = expandedCard === card.id;
            return (
              <div
                key={card.id}
                onClick={() => toggleCard(card.id)}
                className={`group relative text-left rounded-xl transition-all duration-500 cursor-pointer border-2 overflow-hidden ${
                  isExpanded
                    ? 'bg-[#1a1a1a] border-[#1a1a1a] shadow-2xl shadow-black/10'
                    : 'bg-white border-[#eee] hover:border-[#F5C518]/40 shadow-sm hover:shadow-lg'
                } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${200 + i * 150}ms` }}
              >
                {/* Active indicator bar */}
                <div
                  className={`absolute top-0 left-6 right-6 h-[3px] rounded-b-full transition-all duration-500 ${
                    isExpanded ? 'bg-[#F5C518] opacity-100' : 'bg-transparent opacity-0'
                  }`}
                ></div>

                {/* Main content - always visible */}
                <div className="p-7 sm:p-9">
                  {/* Icon */}
                  <div
                    className={`w-14 h-14 rounded-lg flex items-center justify-center mb-6 transition-all duration-500 ${
                      isExpanded ? 'bg-[#F5C518]/15' : 'bg-[#F5C518]/10 group-hover:bg-[#F5C518]/15'
                    }`}
                  >
                    <i
                      className={`${card.icon} text-2xl text-[#F5C518] transition-colors duration-500`}
                    ></i>
                  </div>

                  {/* Title */}
                  <h3
                    className={`text-3xl sm:text-4xl font-display tracking-wider mb-2 transition-colors duration-500 ${
                      isExpanded ? 'text-[#F5C518]' : 'text-[#1a1a1a]'
                    }`}
                  >
                    {card.title}
                  </h3>

                  {/* Subtitle */}
                  <p
                    className={`text-sm font-semibold transition-colors duration-500 ${
                      isExpanded ? 'text-white' : 'text-[#1a1a1a]'
                    }`}
                  >
                    {card.subtitle}
                  </p>

                  {/* Expand indicator when collapsed */}
                  <div
                    className={`flex items-center gap-2 mt-4 transition-all duration-500 ${
                      isExpanded ? 'opacity-0 h-0 mt-0' : 'opacity-100'
                    }`}
                  >
                    <span className="text-[#999] text-xs tracking-wide">Tap to learn more</span>
                    <i className="ri-arrow-down-s-line text-[#999] text-sm"></i>
                  </div>

                  {/* Expandable content */}
                  <div
                    className={`transition-all duration-500 ease-in-out overflow-hidden ${
                      isExpanded ? 'max-h-[600px] opacity-100 mt-5' : 'max-h-0 opacity-0 mt-0'
                    }`}
                  >
                    {/* Description */}
                    <p className="text-[13px] sm:text-[14px] leading-relaxed mb-6 text-white/65">
                      {card.description}
                    </p>

                    {/* Accent line */}
                    <div className="flex items-center gap-3 pt-5 border-t border-white/10">
                      <div className="w-2 h-2 rounded-full bg-[#F5C518] flex-shrink-0"></div>
                      <p className="text-xs font-medium tracking-wide text-[#F5C518]">
                        {card.accent}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Toggle arrow */}
                <div
                  className={`absolute top-7 right-7 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
                    isExpanded
                      ? 'bg-[#F5C518] opacity-100'
                      : 'bg-[#eee] opacity-0 group-hover:opacity-100'
                  }`}
                >
                  <i
                    className={`transition-transform duration-500 text-sm ${
                      isExpanded
                        ? 'ri-arrow-up-s-line text-[#1a1a1a]'
                        : 'ri-arrow-down-s-line text-[#555]'
                    }`}
                  ></i>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
